import { useChat } from "@ai-sdk/react";
import type {
  ToolCallError,
  ToolCallExecutionDetails,
} from "@repo/app-types/api/interviewLivekitAgent";
import type { PlaybookTestCallSetup } from "@repo/app-types/entities/playbookTestCase";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { TOOL_RESULT_QUERY_TOOL_NAME } from "@repo/app-types/entities/tools/toolResultQuery";
import { safeParseJSON } from "@repo/app-types/helpers/utilities";
import { ChatComposer } from "@repo/ui/chat/chatComposer";
import {
  ChatMessage,
  type ChatMessageRenderTextArgs,
} from "@repo/ui/chat/chatMessage";
import { ChatThread } from "@repo/ui/chat/chatThread";
import {
  ChatToolBase,
  ChatToolDisclosure,
  ChatToolStatusIcon,
  type ToolComponent,
} from "@repo/ui/chat/chatToolCall";
import { MessageItem } from "@repo/ui/chat/messageItem";
import { Size } from "@repo/ui/helpers/helpers";
import * as Sentry from "@sentry/react";
import { IconRefresh } from "@tabler/icons-react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { Button as AriaButton } from "react-aria-components";
import { v4 as uuidv4 } from "uuid";
import {
  apiBaseUrl,
  getApiAuthorizationHeaderValue,
} from "../../api/shared.js";
import { TranscriptCallMarker } from "../../components/transcript/transcriptCallMarker.js";
import {
  ToolCallDetail,
  ToolCallExecutionDetailsView,
} from "../tools/components/toolCallExecutionDetails.js";
import {
  checkSimulationCallEnded,
  replaceFailedSimulationTurn,
} from "./teammateAgentSimulationMessages.js";

interface TeammateAgentSimulationProps {
  teammateAgentId: TeammateId;
  shouldUseLiveToolCalls: boolean;
  /**
   * What the agent is told about the call; null for a caller it knows nothing
   * about. Every turn sends it, so it has to be the setup this conversation was
   * started with rather than one still being edited.
   */
  callSetup: PlaybookTestCallSetup | null;
}

/**
 * "Try this agent": a text conversation with a teammate voice agent. The
 * conversation lives here and nowhere else — every turn sends it back to the
 * agent's simulation route — so remounting starts over.
 */
export const TeammateAgentSimulation: FC<TeammateAgentSimulationProps> = (
  props,
) => {
  const { teammateAgentId, shouldUseLiveToolCalls, callSetup } = props;
  // One id for the whole conversation: the server groups its turns under it.
  const [conversationId] = useState(() => uuidv4());

  // Read at send time so toggling live tool calls does not rebuild the chat.
  const shouldUseLiveToolCallsRef = useRef(shouldUseLiveToolCalls);
  shouldUseLiveToolCallsRef.current = shouldUseLiveToolCalls;

  // The call setup is the one this conversation was started with, so the ref
  // only gives the transport a stable read; a settled edit remounts instead.
  const callSetupRef = useRef(callSetup);
  callSetupRef.current = callSetup;

  const transport = useMemo(
    () =>
      new DefaultChatTransport<UIMessage>({
        api: new URL(
          `/agents/${encodeURIComponent(teammateAgentId)}/simulation`,
          apiBaseUrl,
        ).toString(),
        prepareSendMessagesRequest: async ({ messages }) => ({
          headers: {
            Authorization: await getApiAuthorizationHeaderValue(),
            "x-alpharun-commit-sha": __COMMIT_SHA__,
          },
          body: {
            conversation_id: conversationId,
            messages,
            use_live_tool_calls: shouldUseLiveToolCallsRef.current,
            call_setup: callSetupRef.current,
          },
        }),
      }),
    [teammateAgentId, conversationId],
  );

  const setMessagesRef = useRef<
    ((updater: (messages: UIMessage[]) => UIMessage[]) => void) | null
  >(null);
  const { messages, sendMessage, regenerate, status, stop, setMessages } =
    useChat<UIMessage>({
      transport,
      // AI SDK only flips `status` on a failure; show it in the thread.
      onError: (error) => {
        Sentry.captureException(error);
        setMessagesRef.current?.((prev) => {
          return replaceFailedSimulationTurn(prev, {
            id: `error-${uuidv4()}`,
            role: "assistant",
            parts: [
              { type: "text", text: `Something went wrong: ${error.message}` },
            ],
          });
        });
      },
    });
  setMessagesRef.current = setMessages;

  // The agent opens the conversation, once (StrictMode mounts twice).
  const hasOpenedRef = useRef(false);
  useEffect(() => {
    if (!hasOpenedRef.current) {
      hasOpenedRef.current = true;
      void sendMessage();
    }
  }, [sendMessage]);

  const isRunning = status === "submitted" || status === "streaming";
  const lastMessageId = messages.at(-1)?.id;
  // Derived, so retrying a turn before the call ended reopens it.
  const hasCallEnded = checkSimulationCallEnded(messages);

  return (
    <ChatThread
      className="-m-4 flex-auto"
      messages={messages}
      isLoading={status === "submitted"}
      renderMessage={(message, index, count) => {
        const isCurrentMessageStreaming =
          isRunning && message.id === lastMessageId;
        return (
          <div className="group">
            <div className="flex flex-col gap-3">
              <ChatMessage
                message={message}
                isStreaming={isCurrentMessageStreaming}
                tools={{
                  byName: {
                    [TOOL_RESULT_QUERY_TOOL_NAME]: StoredResultLookup,
                  },
                  Fallback: SimulatedToolCall,
                }}
                renderText={renderSimulationText}
              />
            </div>
            {message.role === "assistant" && !isCurrentMessageStreaming ? (
              <div className="mt-0.5 pl-1 opacity-50 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                <AriaButton
                  aria-label="Retry this turn and discard later messages"
                  className="inline-flex size-6 items-center justify-center rounded bg-transparent text-muted-foreground outline-none hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary disabled:cursor-default disabled:bg-transparent disabled:opacity-40"
                  isDisabled={isRunning}
                  onPress={() => {
                    void regenerate({ messageId: message.id });
                  }}
                >
                  <IconRefresh className="size-3.5" />
                </AriaButton>
              </div>
            ) : null}
            {hasCallEnded && index === count - 1 ? (
              <TranscriptCallMarker type="ended" />
            ) : null}
          </div>
        );
      }}
      composer={
        <div className="border-t border-border-light bg-white p-4">
          <ChatComposer
            size={Size.SMALL}
            placeholder={
              hasCallEnded ? "The call has ended" : "Say something to the agent"
            }
            isDisabled={hasCallEnded}
            isRunning={isRunning}
            onStop={stop}
            onSend={(text) => {
              void sendMessage({ text });
            }}
          />
        </div>
      }
    />
  );
};

function renderSimulationText({ text, role }: ChatMessageRenderTextArgs) {
  if (!text) {
    return null;
  }
  return (
    <MessageItem role={role === "user" ? "user" : "assistant"}>
      {text}
    </MessageItem>
  );
}

/** What the simulated tool produced, as the runtime shapes it. */
interface SimulatedToolOutput {
  content?: string;
  alternativeContent?: string;
  isError?: boolean;
  error?: ToolCallError;
  executionDetails?: ToolCallExecutionDetails;
  isEndOfCall?: boolean;
}

interface StoredResultLookupInput {
  result_id?: string;
  query?: string;
}

interface StoredResultLookupOutput {
  answer?: string;
}

const StoredResultLookup: ToolComponent<
  StoredResultLookupInput,
  SimulatedToolOutput
> = (props) => {
  const { input, output, errorText, status } = props;
  const label =
    errorText ?? output?.alternativeContent ?? "Looking up a stored result.";

  // A plain row until the answer is in, like the other simulated tools; the
  // disclosure would otherwise open itself while running.
  if (status.type !== "complete") {
    return <ChatToolBase status={status}>{label}</ChatToolBase>;
  }

  const answer = formatStoredResultAnswer(output?.content, errorText);
  return (
    <ChatToolDisclosure status={status} label={label}>
      <div className="space-y-3 text-xs text-foreground">
        <ToolCallDetail
          label="Source result ID"
          value={input.result_id ?? "(Unknown result ID)"}
        />
        <ToolCallDetail
          label="Query sent"
          value={input.query ?? "(Unknown query)"}
        />
        <ToolCallDetail label="Extracted answer" value={answer} />
      </div>
    </ChatToolDisclosure>
  );
};

const SimulatedToolCall: ToolComponent<unknown, SimulatedToolOutput> = ({
  toolName,
  output,
  errorText,
  status,
}) => {
  const label =
    errorText ??
    output?.alternativeContent ??
    output?.content ??
    `Calling ${toolName}`;
  const executionDetails = output?.executionDetails;
  const error = output?.error;

  if (!executionDetails && !error) {
    return <ChatToolBase status={status}>{label}</ChatToolBase>;
  }

  // A failed tool still answers the agent, so the call completes; only the
  // icon says it failed.
  const icon = output?.isError ? (
    <span className="mt-0.5 shrink-0">
      <ChatToolStatusIcon status={{ type: "incomplete" }} />
    </span>
  ) : undefined;
  const isCodeRun = executionDetails?.transport === "code";

  return (
    <ChatToolDisclosure status={status} icon={icon} label={label}>
      <div className="space-y-3 text-xs text-foreground">
        {isCodeRun ? (
          <ToolCallDetail label="Result" value={output?.content || "(empty)"} />
        ) : null}
        {error ? <ToolCallDetail label="Error" value={error.message} /> : null}
        {executionDetails ? (
          <ToolCallExecutionDetailsView executionDetails={executionDetails} />
        ) : null}
      </div>
    </ChatToolDisclosure>
  );
};

function formatStoredResultAnswer(
  content: string | undefined,
  errorText: string | undefined,
): string {
  if (errorText) {
    return errorText;
  }
  if (!content) {
    return "(No answer yet.)";
  }

  const parsed = safeParseJSON<StoredResultLookupOutput>(content);
  return typeof parsed?.answer === "string" ? parsed.answer : content;
}
